import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

interface FAQItemProps {
  question: string;
  answer: string | JSX.Element;
}

const FAQSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(0);

  const faqData: FAQItemProps[] = [
    {
      question: "What services and products does Trade Simple offer?",
      answer: (
        <>
          Trade Simple offers three core products designed to help traders of all levels achieve consistency and success in the markets:
          <ol style={{ marginTop: 10, paddingLeft: 20 }}>
            <li>Trade Striker Pro</li>
            <li>Trade Simple Algo</li>
            <li>Trade Simple Mentorship</li>
          </ol>
        </>
      )
    },
    {
      question: "What is Trade Striker Pro?",
      answer: "Trade Striker Pro is an advanced automated trading system built specifically for NinjaTrader and optimized for futures trading. It provides precise trade setups, smart market analysis tools, and fully automated strategy execution to help traders identify and capitalize on high-probability opportunities with confidence."
    },
    {
      question: "What is Trade Simple Algo?",
      answer: "Trade Simple Algo is a powerful algorithmic indicator for TradingView that delivers real-time buy and sell signals, supports multiple markets including stocks, options, and futures, and simplifies technical analysis to help traders make faster and more accurate trading decisions."
    },
    {
      question: "What is Trade Simple Mentorship?",
      answer: "Trade Simple Mentorship is a comprehensive mentorship and education program where traders learn proven strategies, receive hands-on coaching through live sessions, and gain ongoing support to build consistency and long-term results."
    },
    {
      question: "Does Trade Simple provide financial advice?",
      answer: "Trade Simple provides tools and insights to support traders in understanding market behavior. It does not offer financial advice or guarantee trading results."
    },
    {
      question: "How do I cancel my subscription?",
      answer: "Log in to your Whop account, navigate to the Dashboard, then click on Manage Memberships to cancel your subscription."
    },
    {
      question: "Is Trade Simple a signal service?",
      answer: "No, Trade Simple is not a signal service. Our focus is on providing real-time market guidance and analysis to help traders understand high-probability setups. We do not issue direct buy or sell signals."
    }
  ];

  const toggleFAQ = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <Container className="apoplsp" id="faq">
      <MainWrapper>
        <Header>
          Frequently Asked <span style={{ color: '#784dcc' }}>Questions</span>
        </Header>
        <Description>
          Find answers to common questions about Trade Simple's plans.
        </Description>

        <FAQList>
          {faqData.map((faq, index) => (
            <FAQItem key={index}>
              <FAQQuestion
                onClick={() => toggleFAQ(index)}
                active={activeIndex === index}
              >
                <QuestionText>{faq.question}</QuestionText>
                <IconWrapper active={activeIndex === index}>
                  <FontAwesomeIcon
                    icon={activeIndex === index ? faChevronUp : faChevronDown}
                  />
                </IconWrapper>
              </FAQQuestion>
              <FAQAnswer active={activeIndex === index}>
                <AnswerContent>
                  {faq.answer}
                </AnswerContent>
              </FAQAnswer>
            </FAQItem>
          ))}
        </FAQList>
      </MainWrapper>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  padding: 0px 20px;
  background: #E7ECF9;
  margin: 0;
`;

const MainWrapper = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const Header = styled.h1`
  text-align: center;
  color: #000;
  font-weight: 500;
  font-size: 45px;
  font-family: var(--font-bold);
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const Description = styled.p`
  text-align: center;
  font-size: 17px;
  color: #666666;
  margin: 0 auto 50px;
  max-width: 700px;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 30px;
  }
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const FAQItem = styled.div`
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
  }
`;

const FAQQuestion = styled.div<{ active: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  cursor: pointer;
  background: ${({ active }) => active ? 'linear-gradient(145deg, #f9f9f9, #ffffff)' : '#fff'};
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(145deg, #f9f9f9, #ffffff);
  }

  @media (max-width: 768px) {
    padding: 15px 20px;
  }
`;

const QuestionText = styled.h3`
  font-size: 18px;
  font-weight: 600;
  font-family: var(--font-semiBold);
  color: #333;
  margin: 0;
  flex: 1;
  padding-right: 15px;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const IconWrapper = styled.div<{ active: boolean }>`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${({ active }) => active ? '#784dcc' : 'linear-gradient(145deg, #f0f0f0, #e0e0e0)'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.3s ease;

  svg {
    font-size: 14px;
    color: ${({ active }) => active ? '#fff' : '#666'};
  }
`;

const FAQAnswer = styled.div<{ active: boolean }>`
  max-height: ${({ active }) => active ? '500px' : '0'};
  overflow: hidden;
  transition: max-height 0.4s ease;
`;

const AnswerContent = styled.div`
  padding: 0 25px 25px 25px;
  font-size: 15px;
  line-height: 1.7;
  color: #666;

  ol {
    margin: 10px 0;
    padding-left: 20px;
    
    li {
      margin-bottom: 8px;
      color: #666;
    }
  }

  @media (max-width: 768px) {
    padding: 0 20px 20px 20px;
    font-size: 14px;
  }
`;

export default FAQSection;

